import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState
} from "react";
import { createPortal } from "react-dom";

import FocusLock from "react-focus-lock";
import type { Instance as PopperInstance } from "@popperjs/core";
import { createPopper } from "@popperjs/core";

import styles from "./Popover.module.css";
import { DimmerVariants, Dimmer } from "../Dimmer";
import { PopperPlacements } from "../types";
import { CATALYST_Z_INDEXES } from "../../constants/styles";
import { keyboardKeys } from "../../constants/keys";
import { HIDE_TOOLTIP_EVENTS, SHOW_TOOLTIP_EVENTS } from "../Tooltip";
import { classNames } from "../../utils/common";

const POPOVER_SKIDDING = 0;
const POPOVER_DISTANCE = 8;

const POPOVER_SHOW_DELAY = 100; // Milliseconds
const POPOVER_HIDE_DELAY = 200; // Milliseconds

export enum PopoverTrigger {
  CLICK = "Click",
  HOVER = "Hover"
}

type PopoverPropsBase = {
  content: string | React.ReactNode;
  children: React.ReactElement;
  placement?: PopperPlacements;
  visible?: boolean;
  onVisibleChange?: (value: boolean) => void;
  "aria-label"?: string;
  borderRadius?: string;
  showArrow?: boolean;
  isPopoverEnabled?: boolean;
  openInPortal?: boolean;
  asChild?: boolean;
  trigger?: PopoverTrigger;
  forceTrapFocus?: boolean;
};

type PopoverProps =
  | (PopoverPropsBase & {
      trigger: PopoverTrigger.HOVER;
      "aria-label"?: string;
    })
  | (PopoverPropsBase & {
      trigger?: PopoverTrigger.CLICK;
      "aria-label": string;
    });

/*

Events summarised

Hover trigger
1. mouse enter/focus in - show
2. mouse leave/focus out - hide

Click trigger
1. click on trigger - show
2. click escape, click on dimmer - hide
3. in case of click trigger we can have action items, buttons hence need focus lock
4. in certain cases for screen reader accuracy(like in color picker, with tooltip beside),
   we need to open popover in portal
5. on close of popover, we need the trigger to be focused again

*/

export const PopoverOld: React.FC<PopoverProps> = props => {
  const { trigger = PopoverTrigger.CLICK } = props;

  const rootElement = document.getElementById("catalyst-popovers-wrapper");
  const isClickTriggerInUse = trigger === PopoverTrigger.CLICK;

  useLayoutEffect(() => {
    if (rootElement) {
      rootElement.setAttribute("role", "presentation");
      rootElement.style.zIndex = CATALYST_Z_INDEXES.DRAWER;
    }
  }, [rootElement]);

  return isClickTriggerInUse ? (
    <ClickPopover {...props} />
  ) : (
    <HoverPopover {...props} />
  );
};

const HoverPopover: React.FC<PopoverProps> = ({
  content,
  children,
  placement = PopperPlacements.TOP,
  "aria-label": ariaLabel,
  borderRadius,
  showArrow = false,
  isPopoverEnabled = true,
  openInPortal = false,
  asChild = false
}) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const popoverArrowRef = useRef<HTMLDivElement>(null);
  const popoverTriggerRef = useRef<HTMLDivElement>(null);
  const popoverInstance = useRef<PopperInstance>();
  const hideTimeout = useRef<NodeJS.Timeout | undefined>();
  const rootElement = document.getElementById("catalyst-popovers-wrapper");

  useLayoutEffect(() => {
    const trigger = popoverTriggerRef.current;
    const popover = popoverContentRef.current;

    if (popover && trigger) {
      popoverInstance.current = createPopper(trigger, popover, {
        strategy: "fixed",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [POPOVER_SKIDDING, POPOVER_DISTANCE]
            }
          }
        ],
        placement
      });
    }
  }, [placement]);

  const showPopover = useCallback(() => {
    // there is an offset distance between popover and content
    // so in case of using hover trigger, we need to avoid its hiding,
    // when mouse moves from trigger to content to avoid flickering
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = undefined;
    }

    setTimeout(() => {
      popoverContentRef.current?.setAttribute("data-show", "");
      popoverArrowRef.current?.setAttribute("data-popper-arrow", "");

      // Enable the event listeners
      popoverInstance.current?.setOptions(options => ({
        ...options,
        modifiers: options.modifiers?.length
          ? [...options.modifiers, { name: "eventListeners", enabled: true }]
          : [{ name: "eventListeners", enabled: true }]
      }));

      // Update its position
      popoverInstance.current?.update();
    }, POPOVER_SHOW_DELAY);
  }, []);

  const hidePopover = useCallback(() => {
    hideTimeout.current = setTimeout(() => {
      popoverContentRef.current?.removeAttribute("data-show");
      popoverArrowRef.current?.removeAttribute("data-popper-arrow");

      // Disable the event listeners
      popoverInstance.current?.setOptions(options => ({
        ...options,
        modifiers: options.modifiers?.length
          ? [...options.modifiers, { name: "eventListeners", enabled: false }]
          : [{ name: "eventListeners", enabled: false }]
      }));
    }, POPOVER_HIDE_DELAY);
  }, []);

  // add tooltip events, click events
  useLayoutEffect(() => {
    if (!isPopoverEnabled) return;

    const popoverTriggerElement = popoverTriggerRef.current;
    const popoverContentElement = popoverContentRef.current;

    SHOW_TOOLTIP_EVENTS.forEach(showTooltipEvent => {
      popoverTriggerElement?.addEventListener(showTooltipEvent, showPopover);
    });
    HIDE_TOOLTIP_EVENTS.forEach(hideTooltipEvent => {
      popoverTriggerElement?.addEventListener(hideTooltipEvent, hidePopover);
    });

    // When using openInPortal, then need this event so even if mouse is in
    // content portal tooltip stays open
    if (openInPortal) {
      popoverContentElement?.addEventListener("mouseenter", showPopover);
      popoverContentElement?.addEventListener("mouseleave", hidePopover);
    }

    return () => {
      SHOW_TOOLTIP_EVENTS.forEach(showTooltipEvent => {
        popoverTriggerElement?.removeEventListener(
          showTooltipEvent,
          showPopover
        );
      });
      HIDE_TOOLTIP_EVENTS.forEach(hideTooltipEvent => {
        popoverTriggerElement?.removeEventListener(
          hideTooltipEvent,
          hidePopover
        );
      });
      if (openInPortal) {
        popoverContentElement?.removeEventListener("mouseenter", showPopover);
        popoverContentElement?.removeEventListener("mouseleave", hidePopover);
      }
    };
  }, [isPopoverEnabled, showPopover, hidePopover, openInPortal]);

  const displayContent = (
    <div
      id="popover-content"
      role="dialog"
      className={classNames(
        styles.catalystPopover,
        styles.catalystHoverPopover
      )}
      ref={popoverContentRef}
      aria-label={ariaLabel}
      style={{ borderRadius }}
    >
      {content}

      {showArrow && (
        <div
          aria-describedby=""
          className={styles.catalystPopoverArrow}
          data-popper-arrow
          ref={popoverArrowRef}
        ></div>
      )}
    </div>
  );

  /**
   * Why should we use createPortal ?
   * when parent has fixed height and overflow hidded, then we need to
   * add content outside the parent, so used openInPortal
   */
  const popoverContent =
    openInPortal && rootElement
      ? createPortal(displayContent, rootElement)
      : displayContent;

  return (
    <>
      {asChild ? (
        <>
          {React.cloneElement(children, {
            ref: popoverTriggerRef
          })}
          {isPopoverEnabled && popoverContent}
        </>
      ) : (
        <div ref={popoverTriggerRef}>
          {isPopoverEnabled && popoverContent}
          {children}
        </div>
      )}
    </>
  );
};

const ClickPopover: React.FC<PopoverProps> = ({
  content,
  children,
  placement = PopperPlacements.TOP,
  visible = false,
  onVisibleChange,
  "aria-label": ariaLabel,
  borderRadius,
  showArrow = false,
  isPopoverEnabled = true,
  openInPortal = false,
  asChild = false,
  forceTrapFocus = false
}) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const popoverArrowRef = useRef<HTMLDivElement>(null);
  const popoverTriggerRef = useRef<HTMLDivElement>(null);
  const popoverInstance = useRef<PopperInstance>();
  const popoverTriggerElementRef = useRef<HTMLElement | null>();
  const rootElement = document.getElementById("catalyst-popovers-wrapper");

  // added additional state so normally code using this component need not have a state
  const [shouldShow, setShouldShow] = useState<boolean>(false);

  useEffect(() => {
    setShouldShow(visible);
  }, [visible]);

  const handleChangeShow = useCallback(
    (isOpen: boolean) => {
      setShouldShow(isOpen);
      onVisibleChange?.(isOpen);
    },
    [onVisibleChange]
  );

  // on close of popover, we need the trigger to be focused again
  useEffect(() => {
    if (shouldShow) {
      popoverTriggerElementRef.current = document.activeElement as HTMLElement;
    } else {
      // return focus to trigger element
      requestAnimationFrame(() => {
        popoverTriggerElementRef.current?.focus();
        popoverTriggerElementRef.current = null;
      });
    }
  }, [shouldShow]);

  const createInstance = useCallback(() => {
    const popover = popoverContentRef.current;
    const trigger = popoverTriggerRef.current;
    if (popover && trigger) {
      popoverInstance.current = createPopper(trigger, popover, {
        strategy: "fixed",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [POPOVER_SKIDDING, POPOVER_DISTANCE]
            }
          }
        ],
        placement
      });
    }
  }, [placement]);

  const destroyInstance = useCallback(() => {
    popoverInstance.current?.destroy();
    popoverInstance.current = undefined;
  }, []);

  const showPopover = useCallback(() => {
    popoverContentRef.current?.setAttribute("show-popper", "");
    popoverArrowRef.current?.setAttribute("data-popper-arrow", "");
    createInstance();
  }, [createInstance]);

  const hidePopover = useCallback(() => {
    popoverContentRef.current?.removeAttribute("show-popper");
    popoverArrowRef.current?.removeAttribute("data-popper-arrow");
    destroyInstance();
  }, [destroyInstance]);

  function togglePopover() {
    if (!isPopoverEnabled) return;
    handleChangeShow?.(shouldShow ? false : true);
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // TODO: remove isPopoverEnabled -  https://thevetted.atlassian.net/browse/PLAT-9824
    if (!isPopoverEnabled) return;
    if (event.key === keyboardKeys.Enter || event.key === keyboardKeys.Space) {
      event.preventDefault();
      handleChangeShow?.(shouldShow ? false : true);
    }
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (shouldShow && event.key === "Escape") {
        event.preventDefault();
        handleChangeShow?.(false);
      }
    };

    if (shouldShow) {
      window.addEventListener("keydown", handleEscapeKey);
    } else {
      window.removeEventListener("keydown", handleEscapeKey);
    }

    return () => {
      window.removeEventListener("keydown", handleEscapeKey);
    };
  }, [shouldShow, handleChangeShow]);

  useLayoutEffect(() => {
    if (shouldShow) {
      showPopover();
    } else {
      hidePopover();
    }
  }, [shouldShow, showPopover, hidePopover]);

  const displayContent = (
    <>
      <Dimmer
        onClick={() => handleChangeShow?.(false)}
        variant={DimmerVariants.TRANSPARENT}
      />
      <div
        id="popover-content"
        role="dialog"
        className={styles.catalystPopover}
        ref={popoverContentRef}
        aria-label={ariaLabel}
        style={{ borderRadius }}
      >
        <FocusLock>
          {content}

          {/* In case popup used as click trigger, then focus lock only works if atleast 1 div tabbale, no ui difference only to make behavior consistent for popovers added this */}
          {forceTrapFocus && <div tabIndex={0} />}
        </FocusLock>
        {showArrow && (
          <div
            aria-describedby=""
            className={styles.catalystPopoverArrow}
            data-popper-arrow
            ref={popoverArrowRef}
          ></div>
        )}
      </div>
    </>
  );

  // Why createPortal needed ??
  // in case of Color picker, when we have tooltip aside the popover
  // screen reader reads the parent when we select color and hit save
  // so to avoid screen reader issues, opened popover separately
  const popoverContent =
    openInPortal && rootElement
      ? createPortal(displayContent, rootElement)
      : displayContent;

  return (
    <>
      {asChild ? (
        React.cloneElement(children, {
          ref: popoverTriggerRef,
          onClick: togglePopover,
          onKeyDown
        })
      ) : (
        <div
          onKeyDown={onKeyDown}
          ref={popoverTriggerRef}
          onClick={togglePopover}
        >
          {children}
        </div>
      )}
      {shouldShow && popoverContent}
    </>
  );
};
